import type {
  DoubaoTtsGenerationView,
  OmniHumanGenerationView,
  OmniHumanProviderConfigView,
  SeedreamImageGenerationView,
  StoryListItem,
} from "@actale/contracts";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  api,
  ApiError,
  storyEntryMediaUrl,
  storyImageUrl,
  storyVoiceAudioUrl,
} from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

const LIGHTHOUSE_STORY_SLUG = "mist-over-lighthouse";
const MIA_SAMPLE_PROMPT = "保持米娅原始相貌、服装和镜头构图。她克制专业但明显担心，先看向远处的码头，再转回镜头说话；自然眨眼和呼吸，不夸张表演，不改变背景，不新增人物。";

const statusLabels: Record<OmniHumanGenerationView["status"], string> = {
  SUBMITTING: "提交中，结果未知",
  QUEUED: "已提交 · 排队中",
  RUNNING: "生成中",
  SUCCEEDED: "已生成 · 待评审",
  FAILED: "失败",
  EXPIRED: "已过期",
};

const errorLabels: Record<string, string> = {
  OMNIHUMAN_PROVIDER_AUTH_FAILED: "AK/SK 或 OmniHuman 权限无效，请检查 AI 模型设置和服务开通状态。",
  OMNIHUMAN_PROVIDER_CONCURRENCY_LIMIT: "供应商当前已有任务在处理，请等待原任务结束。",
  OMNIHUMAN_PROVIDER_CONTENT_REJECTED: "人物图、语音或生成结果触发了供应商内容审核。",
  OMNIHUMAN_PROVIDER_INVALID_RESPONSE: "供应商返回内容无法识别。",
  OMNIHUMAN_PROVIDER_REQUEST_UNKNOWN: "网络中断，供应商是否已受理无法确认。系统不会自动重试，请先检查火山任务记录。",
  OMNIHUMAN_SUBMISSION_FAILED: "提交供应商失败。系统不会自动重试。",
  OMNIHUMAN_PROVIDER_EMPTY_VIDEO: "供应商任务完成，但没有返回视频。",
  OMNIHUMAN_RESULT_AUDIO_MISSING: "返回视频没有可验证的同步音轨，已拒绝入库。",
  OMNIHUMAN_RESULT_MEDIA_INVALID: "返回视频无法通过 MP4 媒体验证。",
  OMNIHUMAN_TASK_NOT_FOUND: "供应商已经找不到这个任务。",
  OMNIHUMAN_TASK_EXPIRED: "供应商结果已过期。",
};

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function replaceGeneration(
  current: OmniHumanGenerationView[],
  next: OmniHumanGenerationView,
): OmniHumanGenerationView[] {
  return [next, ...current.filter((item) => item.id !== next.id)];
}

function durationLabel(seconds: number | null): string {
  return seconds === null ? "未检测" : `${seconds.toFixed(1)} 秒`;
}

export function OmniHumanGenerationPage({
  onConfigureProvider,
}: {
  onConfigureProvider?: () => void;
}): React.ReactNode {
  const [config, setConfig] = useState<OmniHumanProviderConfigView | null>(null);
  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [images, setImages] = useState<SeedreamImageGenerationView[]>([]);
  const [audios, setAudios] = useState<DoubaoTtsGenerationView[]>([]);
  const [generations, setGenerations] = useState<OmniHumanGenerationView[]>([]);
  const [storyId, setStoryId] = useState("");
  const [imageGenerationId, setImageGenerationId] = useState("");
  const [audioGenerationId, setAudioGenerationId] = useState("");
  const [label, setLabel] = useState("米娅口型样片 · 第一版");
  const [prompt, setPrompt] = useState(MIA_SAMPLE_PROMPT);
  const [costConfirmed, setCostConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshingId, setRefreshingId] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextConfig, storyResult, nextImages, nextAudios, nextGenerations] = await Promise.all([
        api.omniHumanProviderConfig(),
        api.storyLines({ pageSize: 100 }),
        api.seedreamImageGenerations(),
        api.doubaoTtsGenerations(),
        api.omniHumanGenerations(),
      ]);
      setConfig(nextConfig);
      setStories(storyResult.data);
      setImages(nextImages);
      setAudios(nextAudios);
      setGenerations(nextGenerations);
      setStoryId((current) => current
        || storyResult.data.find((story) => story.slug === LIGHTHOUSE_STORY_SLUG)?.id
        || storyResult.data[0]?.id
        || "");
    } catch (caught) {
      setError(message(caught, "无法读取人物视频工作台"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const imageCandidates = useMemo(
    () => images.filter((item) => item.story.id === storyId && item.status === "SUCCEEDED" && item.managedImageKey),
    [images, storyId],
  );
  const audioCandidates = useMemo(
    () => audios.filter((item) => item.story.id === storyId && item.status === "SUCCEEDED" && item.managedAudioKey),
    [audios, storyId],
  );

  useEffect(() => {
    setImageGenerationId((current) => imageCandidates.some((item) => item.id === current)
      ? current
      : imageCandidates[0]?.id ?? "");
    setAudioGenerationId((current) => audioCandidates.some((item) => item.id === current)
      ? current
      : audioCandidates[0]?.id ?? "");
    setCostConfirmed(false);
  }, [imageCandidates, audioCandidates]);

  const selectedStory = stories.find((story) => story.id === storyId) ?? null;
  const selectedImage = imageCandidates.find((item) => item.id === imageGenerationId) ?? null;
  const selectedAudio = audioCandidates.find((item) => item.id === audioGenerationId) ?? null;
  const sourceReady = Boolean(
    selectedImage
    && selectedAudio
    && selectedAudio.managedAudioDurationSeconds !== null
    && selectedAudio.managedAudioDurationSeconds > 0
    && selectedAudio.managedAudioDurationSeconds < 35,
  );
  const ready = Boolean(
    config?.configured
    && selectedStory
    && sourceReady
    && label.trim()
    && prompt.trim()
    && prompt.trim().length <= 500
    && costConfirmed
    && !submitting,
  );

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || !selectedStory || !selectedImage || !selectedAudio) return;
    const duration = durationLabel(selectedAudio.managedAudioDurationSeconds);
    if (!window.confirm(`确认付费生成“${label.trim()}”？\n\n故事线：《${selectedStory.title}》\n人物图：${selectedImage.label}\n语音：${selectedAudio.label}（${duration}）\n模型：OmniHuman 1.5\n输出：720P 有声 MP4\n\n本次会把这张人物图和这条最终语音发送给火山。费用按供应商账号实际账单为准；系统不会自动重试，也不会修改故事草稿或发布 StoryVersion。`)) return;
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const next = await api.createOmniHumanGeneration({
        clientRequestId: crypto.randomUUID(),
        storyId,
        label: label.trim(),
        imageGenerationId: selectedImage.id,
        audioGenerationId: selectedAudio.id,
        prompt: prompt.trim(),
        costConfirmed: true,
      });
      setGenerations((current) => replaceGeneration(current, next));
      setCostConfirmed(false);
      if (next.status === "QUEUED" || next.status === "RUNNING") {
        setNotice("任务已被 OmniHuman 接收。等待几分钟后，点击任务上的“查询结果”；系统不会自动再次提交。 ");
      } else if (next.status === "FAILED") {
        setError(errorLabels[next.errorCode ?? ""] ?? "人物视频提交失败，请查看任务记录。 ");
      }
    } catch (caught) {
      setError(message(caught, "人物视频提交失败"));
      try {
        setGenerations(await api.omniHumanGenerations());
      } catch {
        // Preserve the submission error.
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function refreshGeneration(id: string): Promise<void> {
    setRefreshingId(id);
    setError("");
    setNotice("");
    try {
      const next = await api.refreshOmniHumanGeneration(id);
      setGenerations((current) => replaceGeneration(current, next));
      if (next.status === "SUCCEEDED") {
        setNotice("视频已转存到 ACTALE，可以直接播放评审；尚未应用到故事草稿。 ");
      } else if (next.status === "QUEUED" || next.status === "RUNNING") {
        setNotice("供应商仍在处理。稍后可以再次查询；这不会重新提交，也不会重复付费。 ");
      } else {
        setError(errorLabels[next.errorCode ?? ""] ?? "人物视频任务未成功完成。 ");
      }
    } catch (caught) {
      setError(message(caught, "查询人物视频结果失败"));
    } finally {
      setRefreshingId("");
    }
  }

  if (loading && !config) {
    return <div className="director-loading"><span /><strong>正在读取人物视频工作台…</strong></div>;
  }
  if (!config) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "人物视频工作台不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  return (
    <div className="omnihuman-page">
      <div className="page-heading provider-config-heading">
        <div><p className="eyebrow">AI CHARACTER VIDEO</p><h1>人物视频</h1><p>把已通过的人物图和最终语音交给 OmniHuman，生成原声驱动的口型、表情与动作样片。</p></div>
        <button className="secondary-button" disabled={loading || submitting} onClick={() => void load()}>{loading ? "正在刷新…" : "刷新工作台"}</button>
      </div>

      {error ? <div className="error-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice" role="status">✓ {notice}</div> : null}

      <form className="omnihuman-form" onSubmit={(event) => void submit(event)}>
        <header>
          <div><span className={`provider-state-dot ${config.configured ? "ready" : "empty"}`} /><div><strong>新建同步人物样片</strong><small>OmniHuman 1.5 · 单图 + 最终语音 · 720P</small></div></div>
          <span className="provider-config-status ready">不会修改故事</span>
        </header>
        {!config.configured ? <div className="tts-config-required" role="alert"><div><strong>OmniHuman 尚未配置</strong><span>请先在“AI 模型设置”中保存 AK/SK。</span></div><button type="button" className="secondary-button" onClick={onConfigureProvider}>前往 AI 模型设置</button></div> : null}

        <div className="omnihuman-grid">
          <label><span>关联故事线</span><select value={storyId} onChange={(event) => setStoryId(event.target.value)} required><option value="">请选择故事线</option>{stories.map((story) => <option key={story.id} value={story.id}>{story.title} · 草稿 R{story.draftRevision}</option>)}</select></label>
          <label><span>任务名称</span><input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={120} required /></label>
          <label><span>已通过的人物图</span><select value={imageGenerationId} onChange={(event) => setImageGenerationId(event.target.value)} required><option value="">请选择已生成图片</option>{imageCandidates.map((item) => <option key={item.id} value={item.id}>{item.label} · {item.managedImageWidth}×{item.managedImageHeight}</option>)}</select><small>只显示这条故事线中生成成功的 Seedream 原创图。</small></label>
          <label><span>已通过的最终语音</span><select value={audioGenerationId} onChange={(event) => setAudioGenerationId(event.target.value)} required><option value="">请选择已生成语音</option>{audioCandidates.map((item) => <option key={item.id} value={item.id}>{item.label} · {durationLabel(item.managedAudioDurationSeconds)}</option>)}</select><small>只显示生成成功的豆包语音；OmniHuman 会按这条声音驱动嘴型。</small></label>
          <label className="omnihuman-prompt"><span>人物表演提示</span><textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={5} maxLength={500} required /><small>{prompt.trim().length} / 500 字符</small></label>
        </div>

        <div className="omnihuman-source-review">
          {selectedImage?.managedImageKey ? <div><span>发送的人物图</span><img src={storyImageUrl(selectedImage.managedImageKey)} alt={`${selectedImage.label}，AI 生成候选图`} /></div> : <div className="omnihuman-source-empty">没有可用人物图</div>}
          {selectedAudio?.managedAudioKey ? <div><span>发送的最终语音 · {durationLabel(selectedAudio.managedAudioDurationSeconds)}</span><audio src={storyVoiceAudioUrl(selectedAudio.managedAudioKey)} controls preload="metadata" /><p>{selectedAudio.text}</p></div> : <div className="omnihuman-source-empty">没有可用最终语音</div>}
        </div>

        {!sourceReady ? <div className="image-rights-note"><strong>素材尚未就绪</strong><span>必须同时选择同一故事线中生成成功的完整人物图，以及已检测时长且小于 35 秒的最终语音。</span></div> : null}
        <div className="tts-model-snapshot"><div><span>模型</span><strong>OmniHuman 1.5</strong></div><div><span>输出</span><strong>720P · 同步有声 MP4</strong></div><div><span>任务方式</span><strong>手动查询 · 不自动重试</strong></div></div>
        <label className="tts-cost-confirm"><input type="checkbox" checked={costConfirmed} onChange={(event) => setCostConfirmed(event.target.checked)} /><span><strong>我确认本次会把所选图片和语音发送给火山，并产生 OmniHuman 视频生成费用</strong><small>具体价格、免费额度和最终金额以火山账号实际账单为准。</small></span></label>
        <footer><span>只会新增一条待评审视频；不会自动用于 APP、故事草稿、开场视频或 StoryVersion。</span><button className="primary-button compact" disabled={!ready}>{submitting ? "正在提交，请勿重复操作…" : "确认付费并提交人物视频"}</button></footer>
      </form>

      <section className="omnihuman-history">
        <header><div><strong>最近人物视频任务</strong><small>提交后手动查询；成功结果会转存到 ACTALE 再供播放</small></div><span>{generations.length} 条</span></header>
        {!generations.length ? <div className="empty-state"><span className="empty-glyph" aria-hidden="true">演</span><h2>还没有人物视频任务</h2><p>配置完成不等于已生成；只有确认付费提交后才会发送人物图和语音。</p></div> : null}
        <div className="omnihuman-list">
          {generations.map((generation) => {
            const videoUrl = generation.managedVideoKey ? storyEntryMediaUrl(generation.managedVideoKey, "mp4") : null;
            const active = generation.status === "SUBMITTING" || generation.status === "QUEUED" || generation.status === "RUNNING";
            return <article className={`omnihuman-item ${generation.status.toLowerCase()}`} key={generation.id}>
              <header><span>{statusLabels[generation.status]}</span><div><strong>{generation.label}</strong><small>《{generation.story.title}》 · {formatUpdatedAt(generation.createdAt)}</small></div>{active ? <button className="secondary-button" disabled={refreshingId === generation.id} onClick={() => void refreshGeneration(generation.id)}>{refreshingId === generation.id ? "正在查询…" : "查询结果"}</button> : null}</header>
              {videoUrl ? <div className="omnihuman-result"><video src={videoUrl} controls playsInline preload="metadata" poster={storyEntryMediaUrl(generation.managedVideoKey!, "jpg")} /><div><strong>待人工评审</strong><span>重点检查嘴型与语音同步、人物是否变脸、表情和动作是否自然。</span><a className="secondary-button" href={videoUrl} target="_blank" rel="noreferrer">打开或下载 MP4</a><small>尚未应用到故事草稿或 APP。</small></div></div> : null}
              {generation.status === "FAILED" || generation.status === "EXPIRED" ? <div className="seedance-job-error"><strong>{generation.errorCode ?? "OMNIHUMAN_GENERATION_FAILED"}</strong><span>{errorLabels[generation.errorCode ?? ""] ?? "请检查供应商状态后再决定是否重新付费提交。"}</span></div> : null}
              {generation.status === "SUBMITTING" ? <div className="tts-pending-note">供应商是否已受理尚未确认。系统不会自动重试，避免重复扣费。</div> : null}
              <details><summary>查看来源和生成参数</summary><dl><div><dt>人物图</dt><dd>{generation.image.label}</dd></div><div><dt>最终语音</dt><dd>{generation.audio.label} · {durationLabel(generation.audio.durationSeconds)}</dd></div><div><dt>输出</dt><dd>{generation.outputResolution}P · {generation.fastMode ? "快速模式" : "标准模式"}</dd></div><div><dt>结果音轨</dt><dd>{generation.managedVideoHasAudio === null ? "未检测" : generation.managedVideoHasAudio ? "已检测" : "缺失"}</dd></div><div className="omnihuman-parameter-wide"><dt>台词</dt><dd>{generation.audio.text}</dd></div><div className="omnihuman-parameter-wide"><dt>表演提示</dt><dd>{generation.prompt ?? "未填写"}</dd></div></dl></details>
            </article>;
          })}
        </div>
      </section>
    </div>
  );
}
